import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../utils/auth";
import { useQuery } from "@tanstack/react-query";
import { fetchNewOrdersSummary, fetchHoldOrdersSummary, fetchOldOrdersSummary, fetchChangesOrdersSummary, fetchDepositRequiredSummary, fetchCustomOrdersSummary } from "../api/OrderAPIs";
import { ChevronDown, ChevronUp } from "lucide-react"; // You need to install lucide-react or use SVG

const SkeletonLoader = ({ type }) => {
  if (type === "card") {
    return (
      <div className="animate-pulse bg-white shadow-md rounded-lg p-6 mb-6">
        <div className="h-6 bg-gray-200 rounded w-1/2 mb-2"></div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="h-6 bg-gray-200 rounded w-3/4"></div>
          <div className="h-6 bg-gray-200 rounded w-3/4"></div>
          <div className="h-6 bg-gray-200 rounded w-3/4"></div>
        </div>
      </div>
    );
  }
  return null;
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { authState, logout } = useAuth();

  // Collapsible states (default: collapsed)
  const [isUnderPurchaseOpen, setIsUnderPurchaseOpen] = useState(false);
  const [isOthersOpen, setIsOthersOpen] = useState(false);
  const [isAdminToolsOpen, setIsAdminToolsOpen] = useState(false);

  const { data: newOrdersSummary, isLoading: isLoadingNew, error: errorNew } = useQuery({
    queryKey: ["newOrdersSummary"],
    queryFn: fetchNewOrdersSummary,
    enabled: authState.role === "Admin",
    staleTime: Infinity,
    placeholderData: { count: 0, totalProfit: 0, totalAmount: 0 },
  });

  const { data: holdOrdersSummary, isLoading: isLoadingHold, error: errorHold } = useQuery({
    queryKey: ["holdOrdersSummary"],
    queryFn: fetchHoldOrdersSummary,
    enabled: authState.role === "Admin",
    staleTime: Infinity,
    placeholderData: { count: 0, totalProfit: 0, totalAmount: 0 },
  });

  const { data: oldOrdersSummary, isLoading: isLoadingOld, error: errorOld } = useQuery({
    queryKey: ["oldOrdersSummary"],
    queryFn: fetchOldOrdersSummary,
    enabled: authState.role === "Admin",
    staleTime: Infinity,
    placeholderData: { count: 0, totalProfit: 0, totalAmount: 0 },
  });

  const { data: changesOrdersSummary, isLoading: isLoadingChanges, error: errorChanges } = useQuery({
    queryKey: ["changesOrdersSummary"],
    queryFn: fetchChangesOrdersSummary,
    enabled: authState.role === "Admin",
    staleTime: Infinity,
    placeholderData: { count: 0, totalProfit: 0, totalAmount: 0 },
  });

  const { data: depositRequiredSummary, isLoading: isLoadingDeposit, error: errorDeposit } = useQuery({
    queryKey: ["depositRequiredSummary"],
    queryFn: fetchDepositRequiredSummary,
    enabled: authState.role === "Admin",
    staleTime: Infinity,
    placeholderData: { count: 0, totalProfit: 0, totalAmount: 0 },
  });

  const { data: customOrdersSummary, isLoading: isLoadingCustom, error: errorCustom } = useQuery({
    queryKey: ["customOrdersSummary"],
    queryFn: fetchCustomOrdersSummary,
    enabled: authState.role === "Admin",
    staleTime: Infinity,
    placeholderData: { count: 0, totalProfit: 0, totalAmount: 0 },
  });

  const isLoading = isLoadingNew || isLoadingHold || isLoadingChanges || isLoadingDeposit || isLoadingCustom || isLoadingOld;
  const error = errorNew || errorHold || errorChanges || errorDeposit || errorCustom || errorOld;

  React.useEffect(() => {
    if (authState.role !== "Admin" && authState.role !== "Moderator") {
      navigate("/login");
    }
  }, [authState, navigate]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6" dir="rtl">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">لوحة التحكم الإدارية</h1>
          <button
            onClick={handleLogout}
            className="bg-red-500 text-white px-4 py-4 rounded hover:bg-red-600"
          >
            تسجيل الخروج
          </button>
        </div>

        {error && (
          <div className="bg-red-100 text-red-700 p-4 rounded mb-6">
            فشل في تحميل ملخصات الطلبات. يرجى المحاولة مرة أخرى.
          </div>
        )}

        {/* === Summary Cards (unchanged) === */}
        {authState.role === "Admin" && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white shadow-md rounded-lg p-6">
              <h2 className="text-lg font-semibold mb-2">ملخص الطلبات الجديدة</h2>
              {isLoading ? (
                <SkeletonLoader type="card" />
              ) : (
                <div>
                  <p>عدد الطلبات: {newOrdersSummary.count}</p>
                  <p>إجمالي المبلغ: LE {newOrdersSummary.totalAmount}</p>
                </div>
              )}
            </div>
            <div className="bg-white shadow-md rounded-lg p-6">
              <h2 className="text-lg font-semibold mb-2">ملخص الطلبات المعلقة</h2>
              {isLoading ? (
                <SkeletonLoader type="card" />
              ) : (
                <div>
                  <p>عدد الطلبات: {holdOrdersSummary.count}</p>
                  <p>إجمالي المبلغ: LE {holdOrdersSummary.totalAmount}</p>
                </div>
              )}
            </div>
            {/* <div className="bg-white shadow-md rounded-lg p-6">
              <h2 className="text-lg font-semibold mb-2">ملخص الطلبات القديمة</h2>
              {isLoading ? (
                <SkeletonLoader type="card" />
              ) : (
                <div>
                  <p>عدد الطلبات: {oldOrdersSummary.count}</p>
                  <p>إجمالي المبلغ: LE {oldOrdersSummary.totalAmount}</p>
                </div>
              )}
            </div> */}
            <div className="bg-white shadow-md rounded-lg p-6">
              <h2 className="text-lg font-semibold mb-2">ملخص الطلبات تحت التغييرات</h2>
              {isLoading ? (
                <SkeletonLoader type="card" />
              ) : (
                <div>
                  <p>عدد الطلبات: {changesOrdersSummary.count}</p>
                  <p>إجمالي المبلغ: LE {changesOrdersSummary.totalAmount}</p>
                </div>
              )}
            </div>
            <div className="bg-white shadow-md rounded-lg p-6">
              <h2 className="text-lg font-semibold mb-2">ملخص الطلبات التي تتطلب دفعة أولية</h2>
              {isLoading ? (
                <SkeletonLoader type="card" />
              ) : (
                <div>
                  <p>عدد الطلبات: {depositRequiredSummary.count}</p>
                  <p>إجمالي المبلغ: LE {depositRequiredSummary.totalAmount}</p>
                </div>
              )}
            </div>
            <div className="bg-white shadow-md rounded-lg p-6">
              <h2 className="text-lg font-semibold mb-2">ملخص طلبات التصاميم المخصصة</h2>
              {isLoading ? (
                <SkeletonLoader type="card" />
              ) : (
                <div>
                  <p>عدد الطلبات: {customOrdersSummary.count}</p>
                  <p>إجمالي المبلغ: LE {customOrdersSummary.totalAmount}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* === Order Management (unchanged) === */}
        <div className="mb-8">
          <h3 className="text-xl font-semibold mb-4 text-center">إدارة الطلبات</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pb-4">
            <button
              onClick={() => navigate("/new-orders")}
              className="bg-blue-500 text-white px-6 py-4 rounded-lg hover:bg-blue-600 text-center transition-colors"
            >
              الطلبات الجديدة
            </button>
            <button
              onClick={() => navigate("/hold-orders")}
              className="bg-yellow-500 text-white px-6 py-4 rounded-lg hover:bg-yellow-600 text-center transition-colors"
            >
              الطلبات المعلقة
            </button>
            <button
              onClick={() => navigate("/changes-orders")}
              className="bg-purple-500 text-white px-6 py-4 rounded-lg hover:bg-purple-600 text-center transition-colors"
            >
              طلبات تحت التغييرات
            </button>
            <button
              onClick={() => navigate("/deposit-required-orders")}
              className="bg-orange-500 text-white px-6 py-4 rounded-lg hover:bg-orange-600 text-center transition-colors"
            >
              طلبات تتطلب دفعة أولية (Bulk)
            </button>
            <button
              onClick={() => navigate("/custom-orders")}
              className="bg-teal-500 text-white px-6 py-4 rounded-lg hover:bg-teal-600 text-center transition-colors"
            >
              طلبات التصاميم المخصصة
            </button>
          </div>
        </div>
        
        {/* === Search Section (unchanged) === */}
        <div className="mb-8">
          <h3 className="text-xl font-semibold mb-4 text-center">البحث</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              onClick={() => navigate("/search-orders")}
              className="bg-indigo-500 text-white px-6 py-4 rounded-lg hover:bg-indigo-600 text-center transition-colors"
            >
              البحث عن طلب
            </button>
            <button
              onClick={() => navigate("/search-designs")}
              className="bg-green-500 text-white px-6 py-4 rounded-lg hover:bg-green-600 text-center transition-colors"
            >
              البحث عن تصميم
            </button>
          </div>
        </div>

        {/* === COLLAPSIBLE: تحت الشراء === */}
        <div className="mb-8">
          <button
            onClick={() => setIsUnderPurchaseOpen(!isUnderPurchaseOpen)}
            className="w-full flex justify-center items-center gap-2 text-xl font-semibold mb-4 hover:text-indigo-600 transition-colors"
          >
            تحت الشراء
            {isUnderPurchaseOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </button>

          <div
            className={`overflow-hidden transition-all duration-300 ease-in-out ${
              isUnderPurchaseOpen ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"
            }`}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pb-4">
              <button
                onClick={() => navigate("/under-purchase-orders")}
                className="bg-indigo-500 text-white px-6 py-4 rounded-lg hover:bg-indigo-600 text-center transition-colors"
              >
                الطلبات تحت الشراء
              </button>
              <button
                onClick={() => navigate("/waiting-confirming-orders")}
                className="bg-yellow-500 text-white px-6 py-4 rounded-lg hover:bg-yellow-600 text-center transition-colors"
              >
                اتوفرت , الانتظار للتأكيد
              </button>
              <button
                onClick={() => navigate("/confirming-orders")}
                className="bg-green-500 text-white px-6 py-4 rounded-lg hover:bg-green-600 text-center transition-colors"
              >
                تم التأكيد , نفذ
              </button>
              <button
                onClick={() => navigate("/canceled-orders")}
                className="bg-red-500 text-white px-6 py-4 rounded-lg hover:bg-red-600 text-center transition-colors"
              >
                هيلغي
              </button>
              <button
                onClick={() => navigate("/confirmed-orders")}
                className="bg-green-500 text-white px-6 py-6 rounded-lg hover:bg-green-600 text-center transition-colors"
              >
                الطلبات المؤكدة
              </button>
            </div>
          </div>
        </div>

        {/* === COLLAPSIBLE: أخرى === */}
        <div className="mb-8">
          <button
            onClick={() => setIsOthersOpen(!isOthersOpen)}
            className="w-full flex justify-center items-center gap-2 text-xl font-semibold mb-4 hover:text-blue-600 transition-colors"
          >
            أخرى
            {isOthersOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </button>
          <div
            className={`overflow-hidden transition-all duration-300 ease-in-out ${
              isOthersOpen ? "max-h-64 opacity-100" : "max-h-0 opacity-0"
            }`}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4 pb-4">
              <button
                onClick={() => navigate("/old-orders")}
                className="bg-gray-500 text-white px-6 py-4 rounded-lg hover:bg-gray-600 text-center transition-colors"
              >
                الطلبات القديمة
              </button>
              <button
                onClick={() => navigate("/spam-orders")}
                className="bg-red-500 text-white px-6 py-4 rounded-lg hover:bg-red-600 text-center transition-colors"
              >
                الطلبات السبام 
                (معندهاش واتساب او بيستعبط)
              </button>
            </div>
          </div>
        </div>

        {/* === COLLAPSIBLE: أدوات الإدارة (Admin only) === */}
        {authState.role === "Admin" && (
          <div className="mb-8">
            <button
              onClick={() => setIsAdminToolsOpen(!isAdminToolsOpen)}
              className="w-full flex justify-center items-center gap-2 text-xl font-semibold mb-4 hover:text-blue-600 transition-colors"
            >
              أدوات الإدارة
              {isAdminToolsOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </button>
            <div
              className={`overflow-hidden transition-all duration-300 ease-in-out ${
                isAdminToolsOpen ? "max-h-64 opacity-100" : "max-h-0 opacity-0"
              }`}
            >
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pb-4">
                <button
                  onClick={() => navigate("/categories")}
                  className="bg-purple-500 text-white px-6 py-4 rounded-lg hover:bg-purple-600 text-center transition-colors"
                >
                  الفئات
                </button>
                <button
                  onClick={() => navigate("/brands")}
                  className="bg-teal-500 text-white px-6 py-4 rounded-lg hover:bg-teal-600 text-center transition-colors"
                >
                  الأنواع
                </button>
                <button
                  onClick={() => navigate("/returns")}
                  className="bg-red-500 text-white px-6 py-4 rounded-lg hover:bg-red-600 text-center transition-colors"
                >
                  الإرجاعات
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;